// ============================================================
// Modal body builders — returned as HTML strings and injected via
// dangerouslySetInnerHTML. Every value comes from the live view model (vm);
// there is no mock data. When a slice is missing, the modal degrades to an
// empty-state message rather than showing placeholder numbers.
// ============================================================
import { getIconUrl, getBarColor } from './data';

// Escape text pulled from the API before it goes into an HTML string.
function esc(s) {
  if (s == null) return '';
  return String(s).replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])
  );
}

function emptyBody(msg) {
  return `<div style="min-height:200px;display:flex;align-items:center;justify-content:center;color:var(--text-muted);font-size:14px;text-align:center;padding:24px;">${esc(
    msg
  )}</div>`;
}

// Contiguous runs of hours with measurable rain, from the hourly forecast.
function computeRainWindows(hours) {
  const windows = [];
  let cur = null;
  (hours || []).forEach((h) => {
    if ((h.rainIn || 0) > 0) {
      if (!cur) cur = { start: h.time, entries: [] };
      cur.entries.push(h);
      cur.end = h.time;
    } else if (cur) {
      windows.push(cur);
      cur = null;
    }
  });
  if (cur) windows.push(cur);
  return windows;
}

export const MODAL_TITLES = {
  radar: 'Interactive Weather Radar',
  lightning: 'Lightning Tracker',
  storms: 'Active Storm Cells',
  alerts: 'Weather Alerts',
  precip: 'Precipitation Detail',
  precipTimeline: 'Hourly Precipitation — Next 24 Hours',
  forecast: 'Extended Forecast',
  conditions: 'Current Conditions',
};

// v3 Icon names for each modal type (used by the Modal header icon tile).
export const MODAL_ICON_NAMES = {
  conditions: 'thermometer',
  radar: 'radar',
  precip: 'droplet',
  precipTimeline: 'clock',
  lightning: 'zap',
  storms: 'cloud-lightning',
  alerts: 'triangle-alert',
  forecast: 'calendar',
};

const SUNRISE_SVG =
  '<svg style="width:22px;height:22px;" viewBox="0 0 24 24"><path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" stroke="var(--orange)" stroke-width="1.5" stroke-linecap="round" fill="none"/><circle cx="12" cy="12" r="4" fill="var(--orange)" opacity="0.8"/></svg>';
const SUNSET_SVG =
  '<svg style="width:22px;height:22px;" viewBox="0 0 24 24"><path d="M12 10V2M4.93 10.93l-1.41-1.41M19.07 9.52l1.41-1.41M2 18h20M7 14a5 5 0 0110 0" stroke="var(--orange)" stroke-width="1.5" stroke-linecap="round" fill="none"/></svg>';

// ---------- Current Conditions ----------
function conditionsBody(vm) {
  const c = vm && vm.conditions;
  if (!c) return emptyBody('Current conditions are not available right now.');

  const tiles = (c.items || [])
    .map(
      ([label, val, color]) => `
      <div style="background:var(--surface-1);border:1px solid var(--border);border-radius:8px;padding:16px;text-align:center;">
        <div style="font-size:10px;text-transform:uppercase;color:var(--text-muted);margin-bottom:6px;letter-spacing:0.5px;">${esc(
          label
        )}</div>
        <div style="font-weight:700;font-size:22px;${
          color ? `color:${color};` : ''
        }">${esc(val)}</div>
      </div>`
    )
    .join('');

  const sun = (svg, label, time) => `
    <div style="display:flex;align-items:center;gap:10px;">
      ${svg}
      <div>
        <div style="font-size:11px;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.5px;">${label}</div>
        <div style="font-size:18px;font-weight:600;">${esc(time)}</div>
      </div>
    </div>`;

  return `
    <div style="display:flex;gap:20px;align-items:flex-start;margin-bottom:24px;">
      ${
        c.icon
          ? `<div style="width:100px;height:100px;flex-shrink:0;"><img src="${getIconUrl(
              c.icon
            )}" alt="" style="width:100%;height:100%;object-fit:contain;"></div>`
          : ''
      }
      <div>
        <div style="font-weight:700;font-size:64px;line-height:1;letter-spacing:-2px;">${esc(
          c.temp
        )}</div>
        <div style="font-size:20px;color:var(--text-secondary);margin-bottom:4px;">${esc(
          c.description
        )}</div>
        ${
          c.feelsLike
            ? `<div style="font-size:14px;color:var(--text-muted);">${esc(
                c.feelsLike
              )}</div>`
            : ''
        }
        <div style="margin-top:8px;font-size:18px;font-weight:600;">
          <span style="color:var(--temp-warm);">${esc(c.high)}</span>
          <span style="color:var(--text-muted);font-weight:400;font-size:14px;"> / </span>
          <span style="color:var(--temp-cool);">${esc(c.low)}</span>
          <span style="font-size:12px;color:var(--text-muted);font-weight:400;margin-left:8px;">High / Low</span>
        </div>
      </div>
    </div>
    <div class="cond-stat-grid" style="margin-bottom:24px;">${tiles}</div>
    <div style="display:flex;justify-content:space-around;padding:16px;background:var(--surface-1);border:1px solid var(--border);border-radius:8px;">
      ${sun(SUNRISE_SVG, 'Sunrise', c.sunrise)}
      ${sun(SUNSET_SVG, 'Sunset', c.sunset)}
    </div>
  `;
}

// ---------- Precipitation Detail ----------
function precipBody(vm) {
  const p = (vm && vm.precip) || {};
  const today = vm && vm.forecast && vm.forecast[0];
  const hours = (today && today.hourly) || [];
  const hasAny = p.last24 != null || p.next24 != null || p.rate || hours.length;
  if (!hasAny)
    return emptyBody('Precipitation data is not available right now.');

  const card = (label, val, color, tint, border) => `
    <div style="background:${tint || 'var(--surface-1)'};border:1px solid ${
    border || 'var(--border)'
  };border-radius:10px;padding:18px 22px;">
      <div style="font-size:10px;color:var(--text-muted);text-transform:uppercase;letter-spacing:1.2px;font-weight:600;margin-bottom:10px;">${label}</div>
      <div style="font-size:34px;font-weight:800;color:${color};line-height:1;font-variant-numeric:tabular-nums;letter-spacing:-1px;">${val}</div>
    </div>`;

  const cards = `
    <div class="precip-stat-grid" style="margin-bottom:18px;">
      ${card(
        'Last 24 Hours',
        p.last24 != null
          ? `${p.last24}<span style="font-size:15px;color:var(--text-muted);font-weight:500;"> in</span>`
          : '—',
        'var(--text-secondary)'
      )}
      ${card(
        'Current Rate',
        p.rate ? esc(p.rate) : 'None',
        'var(--rain-moderate)'
      )}
      ${card(
        'Next 24 Hours',
        p.next24 != null
          ? `${p.next24}<span style="font-size:15px;color:var(--rain-moderate);font-weight:600;"> in</span>`
          : '—',
        'var(--rain-moderate)',
        'rgba(33,150,243,0.08)',
        'rgba(33,150,243,0.28)'
      )}
    </div>`;

  const nextRain = `
    <div style="background:rgba(33,150,243,0.05);border:1px solid rgba(33,150,243,0.22);border-radius:10px;padding:16px 20px;margin-bottom:18px;font-size:14px;color:var(--text-secondary);">
      ${
        p.nextRainAt
          ? `Next rain expected around <strong style="color:var(--rain-moderate);font-weight:700;">${esc(
              p.nextRainAt
            )}</strong>`
          : 'No rain expected in the forecast period.'
      }
    </div>`;

  const windows = computeRainWindows(hours);
  const windowsHtml = windows.length
    ? windows
        .map((w) => {
          const total = w.entries.reduce((s, h) => s + (h.rainIn || 0), 0);
          const peak = w.entries.reduce((a, b) =>
            (a.rainIn || 0) > (b.rainIn || 0) ? a : b
          );
          const color = getBarColor(peak.rainIn || 0);
          return `
        <div style="display:flex;align-items:center;gap:14px;padding:14px 18px;background:var(--surface-1);border:1px solid var(--border);border-radius:8px;">
          <div style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;box-shadow:0 0 0 3px rgba(33,150,243,0.18);"></div>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:700;font-size:15px;font-variant-numeric:tabular-nums;">${esc(
              w.start
            )} &mdash; ${esc(w.end)}</div>
            <div style="font-size:11px;color:var(--text-muted);margin-top:2px;">Peak ${(
              peak.rainIn || 0
            ).toFixed(2)} in/hr at ${esc(peak.time)}</div>
          </div>
          <div style="text-align:right;flex-shrink:0;">
            <div style="font-weight:800;font-size:18px;color:${color};font-variant-numeric:tabular-nums;line-height:1;">${total.toFixed(
            2
          )}<span style="font-size:11px;color:var(--text-muted);font-weight:500;"> in</span></div>
            <div style="font-size:9px;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.6px;font-weight:600;margin-top:3px;">Total</div>
          </div>
        </div>`;
        })
        .join('')
    : '<div style="font-size:13px;color:var(--text-muted);padding:8px 2px;">No rain windows in the forecast period.</div>';

  return `
    ${cards}
    ${nextRain}
    <div>
      <div style="font-size:10px;color:var(--text-muted);text-transform:uppercase;letter-spacing:1.5px;font-weight:700;margin-bottom:10px;">Expected Rain Windows</div>
      <div style="display:flex;flex-direction:column;gap:8px;">${windowsHtml}</div>
    </div>
  `;
}

// ---------- Hourly Precipitation timeline ----------
function precipTimelineBody(vm) {
  const today = vm && vm.forecast && vm.forecast[0];
  const hours = (today && today.hourly) || [];
  if (!hours.length)
    return emptyBody('Hourly precipitation is not available right now.');

  const barWidth = 46;
  const maxAmt = Math.max(0.1, ...hours.map((h) => h.rainIn || 0));
  const chart =
    `<div style="display:flex;align-items:flex-end;gap:4px;height:200px;min-width:${
      hours.length * (barWidth + 4)
    }px;padding-bottom:28px;position:relative;">` +
    hours
      .map((h) => {
        const amt = h.rainIn || 0;
        const height = amt > 0 ? Math.max(6, (amt / maxAmt) * 100) : 2;
        const color = getBarColor(amt);
        return (
          `<div style="width:${barWidth}px;flex-shrink:0;display:flex;flex-direction:column;align-items:center;height:100%;justify-content:flex-end;position:relative;">` +
          (amt > 0
            ? `<div style="font-size:10px;font-weight:700;color:var(--rain-moderate);margin-bottom:3px;">${amt.toFixed(
                2
              )}"</div>`
            : '') +
          `<div style="width:100%;height:${height}%;background:${color};border-radius:3px 3px 0 0;min-height:2px;"></div>` +
          `<div style="position:absolute;bottom:0;font-size:10px;font-weight:600;color:var(--text-muted);white-space:nowrap;">${esc(
            h.time
          )}</div>` +
          `</div>`
        );
      })
      .join('') +
    `</div>`;

  const windows = computeRainWindows(hours);
  const periodsHtml = windows.length
    ? windows
        .map((w, pi) => {
          const total = w.entries.reduce((s, h) => s + (h.rainIn || 0), 0);
          const peak = w.entries.reduce((a, b) =>
            (a.rainIn || 0) > (b.rainIn || 0) ? a : b
          );
          const pmax = Math.max(0.1, ...w.entries.map((e) => e.rainIn || 0));
          // Per-bar value labels only when the window is short enough to fit
          // them; otherwise they'd force the flex row wider than the card.
          const showVals = w.entries.length <= 6;
          const bars = w.entries
            .map((e) => {
              const amt = e.rainIn || 0;
              const hgt = Math.max(12, (amt / pmax) * 100);
              const c = getBarColor(amt);
              return `<div style="flex:1;min-width:0;display:flex;flex-direction:column;align-items:center;height:100%;justify-content:flex-end;">
                ${
                  showVals
                    ? `<div style="font-size:10px;font-weight:700;color:${c};margin-bottom:3px;">${amt.toFixed(
                        2
                      )}"</div>`
                    : ''
                }
                <div style="width:100%;height:${hgt}%;background:${c};border-radius:3px 3px 0 0;min-height:6px;"></div>
              </div>`;
            })
            .join('');
          // Thin the axis to ~8 labels max so a long window stays readable;
          // shown labels can visually overflow their (min-width:0) slot into the
          // blank neighbors without widening the row.
          const labelEvery = Math.ceil(w.entries.length / 8);
          const labels = w.entries
            .map((e, i) => {
              const show = w.entries.length <= 8 || i % labelEvery === 0;
              return `<div style="flex:1;min-width:0;text-align:center;font-size:10px;color:var(--text-muted);white-space:nowrap;">${
                show ? esc(e.time) : ''
              }</div>`;
            })
            .join('');
          return `
        <div style="background:var(--surface-1);border:1px solid var(--border);border-radius:8px;padding:16px;${
          pi > 0 ? 'margin-top:10px;' : ''
        }">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:14px;">
            <div style="font-weight:700;font-size:15px;color:var(--text-primary);">${esc(
              w.start
            )} &mdash; ${esc(w.end)}</div>
            <div style="font-size:13px;color:var(--text-muted);">Total: <span style="color:var(--rain-moderate);font-weight:700;">${total.toFixed(
              2
            )} in</span></div>
          </div>
          <div style="display:flex;align-items:flex-end;gap:4px;height:100px;">${bars}</div>
          <div style="display:flex;margin-top:6px;">${labels}</div>
          <div style="display:flex;justify-content:space-between;margin-top:10px;font-size:12px;color:var(--text-muted);padding-top:8px;border-top:1px solid var(--border);">
            <span>Peak: <span style="color:var(--rain-heavy);font-weight:700;">${(
              peak.rainIn || 0
            ).toFixed(2)} in/hr</span> at ${esc(peak.time)}</span>
          </div>
        </div>`;
        })
        .join('')
    : '<div style="font-size:13px;color:var(--text-muted);padding:8px 2px;">No rain expected in the forecast period.</div>';

  return `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
      <div style="display:flex;gap:16px;flex-wrap:wrap;">
        ${[
          ['Light', 'var(--rain-light)'],
          ['Moderate', 'var(--rain-moderate)'],
          ['Heavy', 'var(--rain-heavy)'],
          ['Extreme', 'var(--rain-extreme)'],
        ]
          .map(
            ([label, color]) =>
              `<div style="display:flex;align-items:center;gap:6px;font-size:13px;color:var(--text-secondary);"><div style="width:12px;height:12px;border-radius:2px;background:${color};"></div> ${label}</div>`
          )
          .join('')}
      </div>
      <div style="font-size:11px;color:var(--text-muted);">Scroll to explore &rarr;</div>
    </div>
    <div style="overflow-x:auto;overflow-y:hidden;-webkit-overflow-scrolling:touch;padding-bottom:8px;margin-bottom:20px;scrollbar-width:thin;scrollbar-color:var(--scrollbar-thumb) transparent;">
      ${chart}
    </div>
    <div style="font-size:12px;text-transform:uppercase;letter-spacing:1px;color:var(--text-muted);margin-bottom:12px;font-weight:600;">Rain Timeline</div>
    <div style="display:flex;flex-direction:column;gap:0;">${periodsHtml}</div>
  `;
}

// ---------- Lightning Tracker ----------
const LIGHTNING_LEVELS = {
  clear: {
    name: 'All Clear',
    level: 'Level 1 of 3',
    color: 'var(--lightning-clear)',
  },
  caution: {
    name: 'Caution',
    level: 'Level 2 of 3',
    color: 'var(--lightning-caution)',
  },
  danger: {
    name: 'Danger',
    level: 'Level 3 of 3',
    color: 'var(--lightning-danger)',
  },
};

function lightningBody(vm, venue) {
  const l = vm && vm.lightning;
  const name = venue || 'this venue';
  const status = (l && l.status) || 'clear';
  const meta = LIGHTNING_LEVELS[status] || LIGHTNING_LEVELS.clear;

  const stat = (val, unit, label, color) => `
    <div style="background:var(--surface-1);border:1px solid var(--border);border-radius:10px;padding:20px 16px;text-align:center;">
      <div style="font-weight:800;font-size:34px;line-height:1;margin-bottom:6px;font-variant-numeric:tabular-nums;letter-spacing:-1px;${
        color ? `color:${color};` : ''
      }">${val}${
    unit
      ? ` <span style="font-size:14px;color:var(--text-muted);font-weight:600;">${unit}</span>`
      : ''
  }</div>
      <div style="font-size:10px;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.8px;font-weight:700;">${label}</div>
    </div>`;

  const heroSub =
    status === 'clear'
      ? `No recent lightning detected near ${esc(name)}`
      : `Lightning detected ${
          l && l.nearest != null ? `within ${l.nearest} miles of ` : 'near '
        }${esc(name)}`;

  const hero = `
    <div style="position:relative;display:flex;align-items:center;gap:18px;padding:22px 24px;border-radius:12px;margin-bottom:22px;background:linear-gradient(135deg, ${
      meta.color
    }22, ${meta.color}0a);border:1px solid ${meta.color}66;overflow:hidden;">
      <div style="width:56px;height:56px;border-radius:14px;background:${
        meta.color
      };display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 0 24px ${
    meta.color
  }55;">
        <svg viewBox="0 0 24 24" fill="white" style="width:32px;height:32px;"><path d="M13 2L4.5 12.5h5L7 22l11-13h-5.5L16 2h-3z"/></svg>
      </div>
      <div style="flex:1;min-width:0;">
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:4px;">
          <div style="font-weight:800;font-size:24px;text-transform:uppercase;color:var(--text-primary);letter-spacing:0.5px;line-height:1;">${
            meta.name
          }</div>
          <div style="font-size:10px;font-weight:800;padding:4px 10px;border-radius:12px;background:${
            meta.color
          }33;color:${
    meta.color
  };text-transform:uppercase;letter-spacing:0.8px;border:1px solid ${
    meta.color
  }66;">${meta.level}</div>
        </div>
        <div style="font-size:14px;color:var(--text-secondary);font-weight:500;">${heroSub}</div>
      </div>
      ${
        l && l.sinceLast != null
          ? `<div style="text-align:right;flex-shrink:0;">
        <div style="font-size:10px;color:var(--text-muted);text-transform:uppercase;letter-spacing:1px;font-weight:700;margin-bottom:4px;">Last Strike</div>
        <div style="font-weight:800;font-size:22px;color:var(--text-primary);line-height:1;font-variant-numeric:tabular-nums;">${l.sinceLast} <span style="font-size:13px;color:var(--text-muted);font-weight:600;">min ago</span></div>
      </div>`
          : ''
      }
    </div>`;

  const stats = `
    <div class="lightning-stat-grid" style="margin-bottom:28px;">
      ${stat(
        l && l.nearest != null ? l.nearest : '—',
        l && l.nearest != null ? 'mi' : '',
        'Nearest Strike'
      )}
      ${stat(
        l && l.sinceLast != null ? l.sinceLast : '—',
        l && l.sinceLast != null ? 'min' : '',
        'Since Last Strike'
      )}
      ${stat(
        l && l.count != null ? l.count : '—',
        '',
        'Strikes &middot; 30mi / 30min'
      )}
      ${stat(
        l && l.bearing ? esc(l.bearing) : '—',
        '',
        'Strike Bearing',
        'var(--lightning-caution)'
      )}
    </div>`;

  const protocol = (key, iconSvg, body, action) => {
    const m = LIGHTNING_LEVELS[key];
    const current = key === status;
    return `
    <div style="position:relative;background:${m.color}${
      current ? '1f' : '10'
    };border:${current ? '2px' : '1.5px'} solid ${m.color}${
      current ? '' : '3a'
    };border-radius:12px;padding:20px 18px;overflow:hidden;${
      current ? `box-shadow:0 0 24px ${m.color}2e;` : ''
    }">
      ${
        current
          ? `<div style="position:absolute;top:10px;right:10px;font-size:9px;font-weight:800;color:white;background:${m.color};padding:3px 8px;border-radius:8px;text-transform:uppercase;letter-spacing:0.8px;">Current</div>`
          : ''
      }
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:12px;">
        <div style="width:36px;height:36px;border-radius:10px;background:${
          m.color
        }2e;display:flex;align-items:center;justify-content:center;flex-shrink:0;">${iconSvg}</div>
        <div>
          <div style="font-weight:800;font-size:15px;color:${
            m.color
          };letter-spacing:0.3px;line-height:1;">${m.name}</div>
          <div style="font-size:10px;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.8px;font-weight:700;margin-top:3px;">${
            m.level
          }</div>
        </div>
      </div>
      <div style="font-size:13px;color:var(--text-secondary);font-weight:500;line-height:1.45;margin-bottom:12px;">${body}</div>
      <div style="padding-top:12px;border-top:1px solid ${m.color}2e;">
        <div style="font-size:10px;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.8px;font-weight:700;margin-bottom:4px;">Action</div>
        <div style="font-size:13px;color:var(--text-primary);font-weight:${
          current ? '700' : '600'
        };line-height:1.35;">${action}</div>
      </div>
    </div>`;
  };

  return `
    ${hero}
    ${stats}
    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;">
      <div style="font-size:13px;text-transform:uppercase;letter-spacing:1.2px;color:var(--text-primary);font-weight:800;">Safety Protocol</div>
      <div style="font-size:11px;color:var(--text-muted);font-weight:600;">Current threat: <span style="color:${
        meta.color
      };font-weight:800;">${meta.name.toUpperCase()}</span></div>
    </div>
    <div class="lightning-protocol-grid" style="margin-bottom:8px;">
      ${protocol(
        'clear',
        '<svg viewBox="0 0 24 24" fill="none" stroke="var(--lightning-clear)" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" style="width:20px;height:20px;"><polyline points="20 6 9 17 4 12"/></svg>',
        'No strikes within <strong style="color:var(--text-primary);font-weight:700;">30 mi</strong> for <strong style="color:var(--text-primary);font-weight:700;">30 min</strong>',
        'Resume normal operations'
      )}
      ${protocol(
        'caution',
        '<svg viewBox="0 0 24 24" fill="white" style="width:20px;height:20px;"><path d="M13 2L4.5 12.5h5L7 22l11-13h-5.5L16 2h-3z"/></svg>',
        'Lightning within <strong style="color:var(--text-primary);font-weight:700;">10 &ndash; 25 mi</strong> &middot; storm approaching',
        'Notify staff &middot; prep shelter routes'
      )}
      ${protocol(
        'danger',
        '<svg viewBox="0 0 24 24" fill="none" stroke="var(--lightning-danger)" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" style="width:20px;height:20px;"><polygon points="12 2 22 20 2 20 12 2" fill="none"/><line x1="12" y1="9" x2="12" y2="14"/><circle cx="12" cy="17.5" r="0.8" fill="var(--lightning-danger)"/></svg>',
        'Lightning within <strong style="color:var(--text-primary);font-weight:700;">10 mi</strong> &middot; imminent strike risk',
        'Halt activities &middot; shelter immediately'
      )}
    </div>
    <div style="display:flex;align-items:flex-start;gap:10px;padding:14px 16px;background:var(--surface-1);border:1px solid var(--border);border-radius:10px;margin-top:16px;">
      <svg viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:16px;height:16px;flex-shrink:0;margin-top:1px;"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
      <div style="font-size:12px;color:var(--text-secondary);line-height:1.5;font-weight:500;"><strong style="color:var(--text-primary);font-weight:700;">30-minute rule:</strong> Once the threat level drops, do not resume outdoor activities for 30 minutes after the last detected strike within 10 miles.</div>
    </div>
  `;
}

// ---------- Active Storm Cells ----------
const STORM_SEV = {
  rotating: { color: 'var(--alert-severe)', shadow: 'rgba(255,109,0,0.18)' },
  hail: { color: 'var(--lightning-caution)', shadow: 'rgba(255,152,0,0.18)' },
  general: { color: 'var(--lightning-clear)', shadow: 'rgba(34,197,94,0.18)' },
};

function stormsBody(vm, venue) {
  const cells = (vm && vm.stormCells) || [];
  const name = venue || 'this venue';
  if (!cells.length) return emptyBody(`No active storm cells near ${name}.`);

  const sev = (color, shadow, label) =>
    `<div style="display:flex;align-items:center;gap:6px;font-size:11px;color:var(--text-secondary);"><div style="width:10px;height:10px;border-radius:50%;background:${color};box-shadow:0 0 0 3px ${shadow};"></div><span style="font-weight:600;">${label}</span></div>`;

  // Arrival timeline (0–2h axis) — only cells with a known ETA are plotted.
  const timed = cells.filter((c) => c.etaMinutes != null);
  const timeline = `
    <div style="background:var(--surface-1);border:1px solid var(--border);border-radius:10px;padding:16px 20px 14px;margin-bottom:20px;">
      <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.2px;color:var(--text-muted);margin-bottom:18px;">Arrival Timeline</div>
      <div style="position:relative;height:18px;">
        <div style="position:absolute;left:0;right:0;top:50%;height:2px;background:var(--surface-3);transform:translateY(-50%);border-radius:1px;"></div>
        <div style="position:absolute;left:0;top:50%;transform:translate(-1px,-50%);width:3px;height:16px;background:var(--lightning-clear);border-radius:1px;box-shadow:0 0 6px rgba(34,197,94,0.5);"></div>
        ${timed
          .map((c) => {
            const pos = Math.min(100, (c.etaMinutes / 120) * 100);
            const s = STORM_SEV[c.severity] || STORM_SEV.general;
            return `<div style="position:absolute;left:${pos}%;top:50%;transform:translate(-50%,-50%);width:14px;height:14px;border-radius:50%;background:${s.color};box-shadow:0 0 0 4px ${s.shadow};"></div>`;
          })
          .join('')}
      </div>
      <div style="display:flex;justify-content:space-between;margin-top:10px;font-size:10px;color:var(--text-muted);font-weight:600;letter-spacing:0.6px;">
        <span style="color:var(--lightning-clear);">NOW</span><span>30 MIN</span><span>1H</span><span>1.5H</span><span>2H</span>
      </div>
    </div>`;

  const cellsHtml = cells
    .map((c) => {
      const s = STORM_SEV[c.severity] || STORM_SEV.general;
      const rotating = c.severity === 'rotating';
      const stats = [
        [
          'Distance',
          c.distanceMi != null
            ? `${c.distanceMi} mi${c.bearing ? ` ${esc(c.bearing)}` : ''}`
            : '—',
          null,
        ],
        [
          'Max Hail',
          c.maxHailIn != null ? `${c.maxHailIn.toFixed(2)} in` : 'None',
          c.maxHailIn != null ? s.color : 'var(--text-muted)',
        ],
        [
          'Rotation',
          rotating ? 'Detected' : 'None',
          rotating ? 'var(--alert-severe)' : 'var(--text-muted)',
        ],
      ]
        .map(
          ([label, val, color]) => `
        <div>
          <div style="font-size:9px;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.6px;font-weight:600;margin-bottom:5px;">${label}</div>
          <div style="font-weight:700;font-size:18px;color:${
            color || 'var(--text-primary)'
          };line-height:1.1;font-variant-numeric:tabular-nums;">${val}</div>
        </div>`
        )
        .join('');

      return `
      <div style="position:relative;background:var(--surface-1);border:${
        rotating ? '1px solid rgba(255,109,0,0.4)' : '1px solid var(--border)'
      };border-radius:10px;padding:18px 20px;${
        rotating ? 'box-shadow:0 0 0 1px rgba(255,109,0,0.1);' : ''
      }">
        <div style="display:flex;align-items:flex-start;gap:14px;margin-bottom:16px;">
          <div style="width:10px;height:10px;border-radius:50%;background:${
            s.color
          };margin-top:7px;flex-shrink:0;box-shadow:0 0 0 4px ${
        s.shadow
      };"></div>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:700;font-size:15px;text-transform:uppercase;letter-spacing:0.4px;line-height:1.2;color:${
              rotating ? 'var(--alert-severe)' : 'var(--text-primary)'
            };">${esc(c.type)}</div>
            ${
              c.motion
                ? `<div style="font-size:12px;color:var(--text-muted);margin-top:4px;">${esc(
                    c.motion
                  )}</div>`
                : ''
            }
          </div>
          ${
            c.eta && c.eta !== '—'
              ? `<div style="text-align:right;flex-shrink:0;">
            <div style="font-size:9px;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.8px;font-weight:600;margin-bottom:2px;">ETA</div>
            <div style="font-weight:800;font-size:24px;color:${
              rotating ? 'var(--alert-severe)' : 'var(--text-primary)'
            };line-height:1;font-variant-numeric:tabular-nums;letter-spacing:-0.5px;white-space:nowrap;">${esc(
                  c.eta
                )}</div>
          </div>`
              : ''
          }
        </div>
        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:16px;padding-top:14px;border-top:1px solid var(--border-default);">${stats}</div>
      </div>`;
    })
    .join('');

  return `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;margin-bottom:18px;">
      <div style="font-size:13px;color:var(--text-secondary);">${
        cells.length
      } active storm cell${cells.length === 1 ? '' : 's'} tracked near ${esc(
    name
  )}</div>
      <div style="display:flex;gap:14px;flex-wrap:wrap;">
        ${sev('var(--lightning-clear)', 'rgba(34,197,94,0.18)', 'General')}
        ${sev('var(--lightning-caution)', 'rgba(255,152,0,0.18)', 'Hail')}
        ${sev('var(--alert-severe)', 'rgba(255,109,0,0.18)', 'Rotating')}
      </div>
    </div>
    ${timed.length ? timeline : ''}
    <div style="display:flex;flex-direction:column;gap:10px;">${cellsHtml}</div>
  `;
}

// ---------- Weather Alerts ----------
function alertsBody(vm) {
  const alerts = (vm && vm.alerts) || [];
  if (!alerts.length) return emptyBody('No active alerts for this venue.');
  return `<div style="display:flex;flex-direction:column;gap:12px;">${alerts
    .map((a) => {
      const meta = [a.county, a.expires ? `Until ${a.expires}` : null]
        .filter(Boolean)
        .map(esc)
        .join(' — ');
      return `
      <div style="background:rgba(250,70,22,0.08);border:1px solid rgba(250,70,22,0.3);border-radius:10px;padding:16px 18px;">
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:${
          a.body ? '12px' : '0'
        };">
          <svg viewBox="0 0 24 24" fill="none" stroke="var(--orange)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:22px;height:22px;flex-shrink:0;"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <div>
            <div style="font-weight:700;font-size:15px;color:var(--text-primary);">${esc(
              a.type
            )}</div>
            ${
              meta
                ? `<div style="font-size:12px;color:var(--text-muted);margin-top:2px;">${meta}</div>`
                : ''
            }
          </div>
        </div>
        ${
          a.body
            ? `<div style="font-size:13px;line-height:1.55;color:var(--text-secondary);">${esc(
                a.body
              )}</div>`
            : ''
        }
      </div>`;
    })
    .join('')}</div>`;
}

// ---------- Extended Forecast ----------
function forecastBody(vm) {
  const days = (vm && vm.forecast) || [];
  if (!days.length) return emptyBody('Forecast is not available right now.');
  return `<div style="display:flex;flex-direction:column;gap:10px;">${days
    .map((d) => {
      const label = d.isToday ? 'Today' : esc(d.day);
      const meta = [
        d.precipPct != null ? `${d.precipPct}% precip` : null,
        d.rain != null ? `${d.rain} in` : null,
        d.wind ? `${esc(d.wind)} mph` : null,
      ]
        .filter(Boolean)
        .join(' · ');
      return `
      <div style="background:var(--surface-1);border:1px solid var(--border);border-radius:10px;padding:16px 18px;">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
          <div style="display:flex;align-items:center;gap:12px;min-width:0;">
            <div style="width:44px;height:44px;flex-shrink:0;">${
              d.icon
                ? `<img src="${getIconUrl(
                    d.icon
                  )}" alt="" style="width:100%;height:100%;object-fit:contain;">`
                : ''
            }</div>
            <div style="min-width:0;">
              <div style="font-weight:700;font-size:16px;color:var(--text-primary);line-height:1.1;">${label}</div>
              <div style="font-size:12px;color:var(--text-muted);margin-top:2px;">${esc(
                d.date
              )}</div>
            </div>
          </div>
          <div style="flex-shrink:0;font-variant-numeric:tabular-nums;white-space:nowrap;">
            <span style="font-weight:800;font-size:22px;color:var(--temp-warm);">${esc(
              d.high
            )}</span>
            <span style="font-size:15px;color:var(--temp-cool);margin-left:8px;">${esc(
              d.low
            )}</span>
          </div>
        </div>
        ${
          d.summary
            ? `<div style="font-size:13px;color:var(--text-secondary);line-height:1.5;margin-top:12px;">${esc(
                d.summary
              )}</div>`
            : ''
        }
        ${
          meta
            ? `<div style="font-size:12px;color:var(--rain-moderate);margin-top:10px;padding-top:10px;border-top:1px solid var(--border);">${meta}</div>`
            : ''
        }
      </div>`;
    })
    .join('')}</div>`;
}

// Returns the inner HTML for a given modal type (excluding 'radar' and 'hourly',
// which are rendered in React). All content is driven by the live view model.
export function buildModalBody(type, vm, venue) {
  switch (type) {
    case 'conditions':
      return conditionsBody(vm);
    case 'precip':
      return precipBody(vm);
    case 'precipTimeline':
      return precipTimelineBody(vm);
    case 'lightning':
      return lightningBody(vm, venue);
    case 'storms':
      return stormsBody(vm, venue);
    case 'alerts':
      return alertsBody(vm);
    case 'forecast':
      return forecastBody(vm);
    default:
      return emptyBody('Detail view is not available.');
  }
}
